import styled from 'styled-components';
import dayjs from 'dayjs';

import Column from 'components/common/Column';
import Row from 'components/common/Row';
import Text from 'components/common/Text';
import CommonImage from 'components/common/CommonImage';
import {
  IconSystemLoading,
  IconWarning,
  ICON_COPY_LIGHT_GRAY,
} from 'assets/icons';
import {
  Order,
  OrderState,
  TransactionState,
  TransactionType,
} from 'types/api';
import { formatPhoneNumber } from 'utils/formatting';

const CanceledNoticeBox = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  margin-bottom: 2px;
`;

const RelatedBox = styled.div`
  position: relative;
`;

const PriceText = styled.p<{ $isCanceled?: boolean }>`
  position: relative;

  text-align: right;
  min-width: 60px;
  line-height: 26px;
  font-size: 18px;
  font-weight: 600;

  white-space: nowrap;

  text-decoration: ${props => (props.$isCanceled ? 'line-through' : 'none')};
  color: ${props => (props.$isCanceled ? '#989BA2' : '#1B1C1E')};
`;

const CanceledTickBox = styled.div`
  position: absolute;
`;

const CanceledInfoText = ({
  transactions,
  items,
}: Pick<Props, 'transactions' | 'items'>) => {
  const hasCanceledRequested = transactions.some(
    transaction =>
      transaction.status === TransactionState.REQUESTED &&
      transaction.type === TransactionType.CANCELLATION,
  );
  const hasCanceled = transactions.some(
    transaction =>
      transaction.status === TransactionState.COMPLETED &&
      transaction.type === TransactionType.CANCELLATION,
  );

  // const hasCanceled = items.some(item => item.dependedItems.length > 0);

  return (
    (hasCanceledRequested || hasCanceled) && (
      <CanceledNoticeBox>
        {hasCanceledRequested && hasCanceled ? (
          <IconWarning width={20} height={20} color="#FF4242" />
        ) : hasCanceled ? (
          <IconWarning width={20} height={20} color="#FF4242" />
        ) : hasCanceledRequested ? (
          <IconSystemLoading width={20} height={20} color="#FF4242" />
        ) : null}

        <Text color="#FF4242" size="13px" weight={500}>
          {hasCanceledRequested && hasCanceled
            ? '취소요청/완료 내역이 있어요.'
            : hasCanceled
            ? '취소완료 내역이 있어요.'
            : hasCanceledRequested
            ? '취소요청 내역이 있어요.'
            : null}
        </Text>
      </CanceledNoticeBox>
    )
  );
};

interface Props extends Order {
  isSelected: boolean;
  handleSelectSaleId: (id: string) => void;
  isMobile: boolean;
}

const SaleCardV2 = ({
  customer,
  id,
  items,
  transactions,
  status,
  isSelected,
  handleSelectSaleId,
  isMobile,
}: Props) => {
  const isCanceled = status === OrderState.CANCELED;
  const paymentStatus = isCanceled ? '결제취소' : '결제완료';

  const optionCount = items.reduce((sum, item) => sum + item.count, 0);
  const totalPrice = items.reduce(
    (sum, item) => sum + Number(item.amount.value),
    0,
  );

  return (
    <Row
      justifyContent="space-between"
      gap="24px"
      style={{
        padding: isMobile ? '16px 20px' : '16px 40px',
        width: '100%',
        backgroundColor: isSelected ? 'rgba(244, 244, 245, 1)' : '#fff',
        cursor: 'pointer',
      }}
      onClick={() => handleSelectSaleId(id)}
    >
      <Column align="flex-start" gap="6px" style={{ width: 'auto' }}>
        {!isCanceled && (
          <CanceledInfoText transactions={transactions} items={items} />
        )}

        <Text
          color={isCanceled ? '#989BA2' : '#46474C'}
          size="16px"
          weight={600}
          style={{ textDecoration: isCanceled ? 'line-through' : 'none' }}
        >
          {/* 상품명 - 2개이상 구매시 외1개로 표시 */}
          {items[0].info?.name}{' '}
          {optionCount > 1 && `외 ${optionCount - 1}개 (총 ${optionCount}건)`}
        </Text>

        <Column align="flex-start" gap="8px">
          {/* 상품명 */}

          <Text
            color={isCanceled ? '#DBDCDF' : '#70737c'}
            size="13px"
            weight={400}
          >
            {items[0].info.product.title}
          </Text>

          <Row
            gap="4px"
            justifyContent="flex-start"
            style={{ flexWrap: 'wrap' }}
          >
            <Text
              color="#70737C"
              style={
                customer.nickname.length > 10 && {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '90px',
                }
              }
            >
              {/* 크루명/ 번호/ 복사버튼 */}
              {customer.nickname}
            </Text>
            <Text color="#70737C">•</Text>
            <Text color="#70737C">
              {formatPhoneNumber(customer.mobileNumber)}
            </Text>
            <Row
              onClick={e => {
                e.stopPropagation();

                navigator.clipboard.writeText(customer.mobileNumber);

                window.showToast('연락처가 복사되었습니다.', 'success', 3000);
              }}
            >
              <CommonImage
                src={ICON_COPY_LIGHT_GRAY}
                style={{ margin: '0 2px 0 4px' }}
              />
              <Text size="10px" weight={400}>
                복사
              </Text>
            </Row>
          </Row>

          <Text color="#989BA2" size="12px" weight={400}>
            {/* 결제일 */}
            {dayjs(transactions[0].firmedAt).format('YY년 MM월 DD일 HH:mm')}
          </Text>
        </Column>
      </Column>

      <Column
        align="flex-end"
        gap="8px"
        style={{ width: 'auto', position: 'relative' }}
      >
        <Text
          color={isCanceled ? 'rgba(255, 66, 66, 1)' : 'rgba(0, 113, 233, 1)'}
          size="11px"
          weight={600}
          style={{ textAlign: 'right', minWidth: '60px', lineHeight: '14px' }}
        >
          {paymentStatus}
        </Text>
        <PriceText $isCanceled={isCanceled}>
          {totalPrice.toLocaleString()}원
        </PriceText>
      </Column>
    </Row>
  );
};
export default SaleCardV2;
